import { PageEmptyState, PeopleOutline, LoadingSpinner } from '@aslanpay/portal-common-lib';
import { Flex, Text, useDisclosure } from '@chakra-ui/react';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { useManagingCompanyStore } from 'app/store';
import { useCallback, useMemo, useState } from 'react';
import PrideEmployeeListModal from 'app/components/Modals/PrideEmployeeListModal';
import CreatePotModal from 'app/components/Modals/CreatePotModal/CreatePotModal';
import { GroupWithPot } from 'app/hooks/useGroupsWithPot/types';
import useGroupWithPotEmployees from 'app/hooks/useGroupsWithPot/useGroupWithPotEmployees';

import PridesContent from './PridesContent/PridesContent';

function Prides() {
  const company = useManagingCompanyStore((state) => state.company);
  const { isLoading, hasGroups, groupsWithPots, setSelectedGroup, modalProps } =
    useGroupWithPotEmployees(company?.id);

  const [createPotGroup, setCreatePotGroup] = useState<GroupWithPot | undefined>();

  const {
    isOpen: isOpenCreatePotModal,
    onClose: onCloseCreatePotModal,
    onOpen: onOpenCreatePotModal,
  } = useDisclosure();

  const onCreatePot = useCallback(
    (group: GroupWithPot) => {
      setCreatePotGroup(group);
      onOpenCreatePotModal();
    },
    [onOpenCreatePotModal]
  );

  const renderContent = useMemo(() => {
    if (isLoading) {
      return (
        <Flex flex="1" flexDir="column" alignItems="center" justifyContent="center">
          <LoadingSpinner />
        </Flex>
      );
    }

    if (!hasGroups) {
      return (
        <PageEmptyState
          icon={<PeopleOutline color="brand.purple" boxSize="40px" />}
          title="No groups found"
          description="No organisational structure was found for your company."
        />
      );
    }

    return (
      <PridesContent
        groups={groupsWithPots}
        setSelectedGroup={setSelectedGroup}
        onCreatePot={onCreatePot}
      />
    );
  }, [isLoading, hasGroups, groupsWithPots, setSelectedGroup, onCreatePot]);

  return (
    <AuthenticatedPageWrapper title="Prides">
      <Flex flexDir="column" gap="40px" h="full">
        <Flex flexDir="column" gap="8px">
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            Prides
          </Text>
          <Text color="brand.darkPurple">
            This is how this company&apos;s people are grouped for reward and recognition.
          </Text>
        </Flex>
        {renderContent}
      </Flex>

      <PrideEmployeeListModal {...modalProps} />

      <CreatePotModal
        isOpen={isOpenCreatePotModal}
        onClose={onCloseCreatePotModal}
        group={createPotGroup}
      />
    </AuthenticatedPageWrapper>
  );
}

export default Prides;
